import type { DatepickerError, CommonDatepickerProps, ExtraSymbolsError } from "./types";
import { transformDateToInputFormat } from "./helpers";

const FIELD_NAME_REPLACER = "_FIELD_NAME_REPLACER_";
const DATE_REPLACER = "_DATE_REPLACER_";
const ERRORS_MAPPER: Record<Exclude<DatepickerError["type"], "extraSymbols">, string> = {
  minDate: `${FIELD_NAME_REPLACER} не может быть раньше ${DATE_REPLACER}`,
  maxDate: `${FIELD_NAME_REPLACER} не может быть позже ${DATE_REPLACER}`,
  disabledDate: `Дата недоступна, выберите другой день`,
  format: `Введите дату в формате ДД.ММ.ГГГГ`,
  emptyDay: `Введите день`,
  emptyMonth: `Введите месяц`,
  emptyYear: `Введите год`,
  invalidDay: `Введите правильный день`,
  invalidMonth: `Введите месяц от 1 до 12`,
  invalidYear: `Введите правильный год в формате ГГГГ`,
};
const EMPTY_PARTS_NAMES = {
  emptyDay: "день",
  emptyMonth: "месяц",
  emptyYear: "год",
};

type Props = {
  errors: DatepickerError[];
  limitations?: {
    minDate?: CommonDatepickerProps["minDate"];
    maxDate?: CommonDatepickerProps["maxDate"];
  };
  fieldName: string;
};

/**
 * Формирует тексты из ошибок возвращаемых _стандартными_ валидаторами
 */
export const getDatepickerError = ({ errors, limitations = {}, fieldName }: Props): string => {
  const { minDate, maxDate } = limitations;

  const extraSymbolsError = errors.find(({ type }) => type === "extraSymbols");
  if (extraSymbolsError) {
    const { part, extraSymbols } = extraSymbolsError as ExtraSymbolsError;
    let fieldText = part ? ERRORS_MAPPER.invalidDay : ERRORS_MAPPER.format;
    if (part === "month") fieldText = ERRORS_MAPPER.invalidMonth;
    if (part === "year") fieldText = ERRORS_MAPPER.invalidYear;
    let symbolsText = `без ${
      extraSymbols.length === 1 ? "символа" : "символов"
    } \u00ab${extraSymbols}\u00bb`;
    return `${fieldText} ${symbolsText}`;
  }

  type EmptyFields = "emptyDay" | "emptyMonth" | "emptyYear";
  const emptyErrors = errors.filter(({ type }) =>
    ["emptyDay", "emptyMonth", "emptyYear"].includes(type)
  );
  if (emptyErrors.length) {
    const fieldsTextArray = emptyErrors.map(
      (error) => EMPTY_PARTS_NAMES[error.type as EmptyFields]
    );
    if (emptyErrors.length === 1 || emptyErrors.length === 2) {
      return `Введите ${fieldsTextArray.join(" и ")}`;
    }
    const [firstField, ...restFields] = fieldsTextArray;
    return `Введите ${firstField}, ${restFields.join(" и ")}`;
  }

  const limitError = errors.find(({ type }) => ["minDate", "maxDate"].includes(type));
  if (limitError) {
    const errorType = limitError.type as "minDate" | "maxDate";
    return ERRORS_MAPPER[errorType]
      .replace(FIELD_NAME_REPLACER, fieldName)
      .replace(
        DATE_REPLACER,
        transformDateToInputFormat(errorType === "minDate" ? minDate! : maxDate!)
      );
  }

  return ERRORS_MAPPER[errors[0].type as Exclude<DatepickerError["type"], "extraSymbols">];
};
